/** Minimum contrast vs surface for spot-picker selection fill — intentionally below WCAG text thresholds. */
const DEFAULT_SURFACE_CONTRAST_THRESHOLD = 1.15;
/** WCAG AA large-text contrast for label on the chosen fill color. */
const TEXT_ON_FILL_CONTRAST_THRESHOLD = 3;
const COLOR_WHITE = '#ffffff';
const COLOR_DARK_GRAY = '#1a1a1a';

function fixHexColor(hexColor: string): string {
  let hex = hexColor;
  if (hex.startsWith('#')) {
    hex = hex.substring(1);
  }
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((el) => `${el}${el}`)
      .join('');
  }
  return `#${hex}`;
}

type RgbTuple = [number, number, number];

function parseHexToRgb(hex: string): RgbTuple | null {
  if (hex.length <= 2) {
    return null;
  }
  const parts = fixHexColor(hex).substring(1).match(/.{2}/g);
  if (!parts || parts.length < 3) {
    return null;
  }
  const [r = '00', g = '00', b = '00'] = parts;
  const channels = [parseInt(r, 16), parseInt(g, 16), parseInt(b, 16)];
  if (channels.some((channel) => Number.isNaN(channel))) {
    return null;
  }
  return channels as RgbTuple;
}

function componentToHex(c: number): string {
  const hex = Number.isFinite(c) ? c.toString(16) : '00';
  return hex.length === 1 ? `0${hex}` : hex;
}

function arrayToHex(arr: RgbTuple): string {
  return `#${componentToHex(arr[0])}${componentToHex(arr[1])}${componentToHex(arr[2])}`;
}

function relativeLuminance(rgb: RgbTuple): number {
  const [r, g, b] = rgb;
  const [channelR = 0, channelG = 0, channelB = 0] = [r, g, b].map((v) => {
    const normalized = v / 255;
    return normalized <= 0.03928 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
  });
  return channelR * 0.2126 + channelG * 0.7152 + channelB * 0.0722;
}

function contrast(rgb1: RgbTuple, rgb2: RgbTuple): number {
  const l1 = relativeLuminance(rgb1) + 0.05;
  const l2 = relativeLuminance(rgb2) + 0.05;
  const result = l2 / l1;
  return result > 1 ? result : 1 / result;
}

function getContrastColor(
  background: string,
  foreground: string,
  fallback: string,
  threshold: number
): string {
  const backgroundRgb = parseHexToRgb(background);
  const foregroundRgb = parseHexToRgb(foreground);
  const fallbackRgb = parseHexToRgb(fallback);
  if (backgroundRgb && foregroundRgb && contrast(backgroundRgb, foregroundRgb) >= threshold) {
    return foreground;
  }
  if (backgroundRgb && fallbackRgb && contrast(backgroundRgb, fallbackRgb) >= threshold) {
    return fallback;
  }
  return COLOR_WHITE;
}

export type BrandColorTriplet = {
  accent: string;
  background: string;
  text: string;
};

export function getContrastingColors(
  accent: string,
  background: string,
  text: string,
  threshold = DEFAULT_SURFACE_CONTRAST_THRESHOLD,
  colorOfBackground = COLOR_WHITE
): { backgroundColor: string; textColor: string } {
  const accentRgb = parseHexToRgb(accent);
  const backgroundRgb = parseHexToRgb(background);
  const textRgb = parseHexToRgb(text);
  const surfaceRgb = parseHexToRgb(colorOfBackground);

  if (surfaceRgb) {
    if (accentRgb && contrast(accentRgb, surfaceRgb) >= threshold) {
      const backgroundColor = arrayToHex(accentRgb);
      return {
        backgroundColor,
        textColor: getContrastColor(
          backgroundColor,
          COLOR_WHITE,
          COLOR_DARK_GRAY,
          TEXT_ON_FILL_CONTRAST_THRESHOLD
        )
      };
    }
    if (textRgb && contrast(textRgb, surfaceRgb) >= threshold) {
      const backgroundColor = arrayToHex(textRgb);
      return {
        backgroundColor,
        textColor: getContrastColor(
          backgroundColor,
          COLOR_WHITE,
          COLOR_DARK_GRAY,
          TEXT_ON_FILL_CONTRAST_THRESHOLD
        )
      };
    }
    if (backgroundRgb && contrast(backgroundRgb, surfaceRgb) >= threshold) {
      const backgroundColor = arrayToHex(backgroundRgb);
      return {
        backgroundColor,
        textColor: getContrastColor(
          backgroundColor,
          COLOR_WHITE,
          COLOR_DARK_GRAY,
          TEXT_ON_FILL_CONTRAST_THRESHOLD
        )
      };
    }
  }
  return {
    backgroundColor: COLOR_DARK_GRAY,
    textColor: COLOR_WHITE
  };
}

export function getContrastingSpotSelectionColor(
  brand: BrandColorTriplet,
  surfaceColor = COLOR_WHITE
): string {
  return getContrastingColors(
    brand.accent,
    brand.background,
    brand.text,
    DEFAULT_SURFACE_CONTRAST_THRESHOLD,
    surfaceColor
  ).backgroundColor;
}

export { parseHexToRgb };
